import { IAuthUserDetails } from '@aqx-core/common/api/auth';
import { ITradingAccount } from '@aqx-core/common/api/user-account';
import mixpanel from '@aqx-core/common/mixpanel';

import { authenticationService } from '../../auth';
import { companyService } from '../../company';
import { companyViewController } from '../../company/controller';
import superPropertiesService from '../superProperties/superPropertiesService';
import { EMixpanelEventNames, EMixpanelProperties } from '../types';
import { IMixpanelUserDetails } from './types';

class UserAnalysisService {
  public getTenantIdFromTraderSubscription = (traderSubcription: string) => {
    const traderSubcriptionArray = traderSubcription.split('|');
    return traderSubcriptionArray[0] ?? '-';
  };

  public identifyUser = (tradingAccount: ITradingAccount) => {
    const mainProductCodeList = companyViewController.getCompanyIntegration();
    const {
      metatraderId,
      accountName,
      leverage,
      marketSubcription,
      platform,
      traderSubscription,
      userGroup,
      baseCurrency,
      balance,
      userId,
      equity,
      marginLevel,
      isDemo
    } = tradingAccount;

    mixpanel.identify(metatraderId.toString());

    const tenantId = undefined;

    const { source, loginAs } = authenticationService.context;

    superPropertiesService.registerSuperProperties({
      [EMixpanelProperties.SOURCE]: source,
      [EMixpanelProperties.TENANT_ID]: tenantId || this.getTenantIdFromTraderSubscription(traderSubscription),
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs,
      [EMixpanelProperties.IS_DEMO]: isDemo,
      [EMixpanelProperties.MAIN_PRODUCT_CODE]: mainProductCodeList ?? '-',
      [EMixpanelProperties.METATRADER_GROUP]: userGroup
    });

    this.setUserProfile({
      [EMixpanelProperties.ACCOUNT_BALANCE]: balance,
      [EMixpanelProperties.ACCOUNT_NAME]: accountName ?? '-',
      [EMixpanelProperties.BASE_CURRENCY]: baseCurrency,
      [EMixpanelProperties.LEVERAGE]: leverage ? leverage : '-',
      [EMixpanelProperties.MARKET_SUBSCRIPTION]: marketSubcription,
      [EMixpanelProperties.NAME]: accountName ?? '-',
      [EMixpanelProperties.PLATFORM]: platform,
      [EMixpanelProperties.TENANT_ID]: tenantId ?? this.getTenantIdFromTraderSubscription(traderSubscription),
      [EMixpanelProperties.TRADER_SUBSCRIPTION]: traderSubscription,
      [EMixpanelProperties.USER_GROUP]: userGroup,
      [EMixpanelProperties.USER_ID]: userId,
      [EMixpanelProperties.EQUITY]: equity,
      [EMixpanelProperties.MARGIN_LEVEL]: marginLevel,
      [EMixpanelProperties.IS_DEMO]: isDemo,
      [EMixpanelProperties.METATRADER_GROUP]: userGroup,
      [EMixpanelProperties.MAIN_PRODUCT_CODE]: mainProductCodeList ?? '-'
    });
  };

  public accountSwitchClicked = () => {
    mixpanel.track(EMixpanelEventNames.ACCOUNT_SWITCH_CLICKED);
  };

  public accountSwitchConfirmClicked = () => {
    mixpanel.track(EMixpanelEventNames.ACCOUNT_SWITCH_CONFIRM_CLICKED);
  };

  public logout = (switchAccount = false) => {
    mixpanel.track(EMixpanelEventNames.ACCOUNT_LOGOUT, {
      [EMixpanelProperties.IS_SWITCH_ACCOUNT]: switchAccount
    });
  };

  public login = (user: IAuthUserDetails) => {
    const tenantId = companyService.getCompanyTenantId();
    const { loginAs } = authenticationService.context || {};
    mixpanel.identify(user?.metatraderId?.toString());
    mixpanel.track(EMixpanelEventNames.ACCOUNT_LOGIN, {
      [EMixpanelProperties.PAGE]: '-',
      [EMixpanelProperties.TENANT_ID]: tenantId ?? '-',
      [EMixpanelProperties.SOURCE]: user.source,
      [EMixpanelProperties.METATRADER_ID]: user.metatraderId,
      [EMixpanelProperties.METATRADER_GROUP]: user.metatraderGroup,
      [EMixpanelProperties.IS_DEMO]: user.isDemo,
      [EMixpanelProperties.TRADER_SUBSCRIPTION]: user.traderSubscription,
      [EMixpanelProperties.MARKET_SUBSCRIPTION]: user.marketSubscription,
      [EMixpanelProperties.MAIN_PRODUCT_CODE]: user.mainProductCode,
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs
    });
  };

  public switchAccount = (tradingAccount: ITradingAccount) => {
    const { loginAs } = authenticationService.context;
    this.logout(true);
    this.identifyUser(tradingAccount);

    mixpanel.track(EMixpanelEventNames.ACCOUNT_SWITCHED, {
      [EMixpanelProperties.PAGE]: '-',
      [EMixpanelProperties.METATRADER_ID]: tradingAccount?.metatraderId,
      [EMixpanelProperties.IS_DEMO]: tradingAccount?.isDemo,
      [EMixpanelProperties.TRADER_SUBSCRIPTION]: tradingAccount?.traderSubscription,
      [EMixpanelProperties.MARKET_SUBSCRIPTION]: tradingAccount?.marketSubcription,
      [EMixpanelProperties.BASE_CURRENCY]: tradingAccount?.baseCurrency,
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs
    });
  };

  public setUserProfile = (propeties: IMixpanelUserDetails) => {
    mixpanel.setUserProfile({ ...propeties });
  };
}

const userAnalysisService = new UserAnalysisService();
export default userAnalysisService;
